import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Select,
    Stack,
    Text,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import usePipelineStore from '../../../../stores/pipeline-store';
import { VarSelect } from '../inputs';

type GenerateStoryStepSettingsProps = {
    stepId: string;
};

export function GenerateStoryStepSettings({ stepId }: GenerateStoryStepSettingsProps) {
    const { pipeline, updateStep } = usePipelineStore((state) => state);
    const step = pipeline.steps[stepId] as GenerateStoryStep;

    const updateGenStoryStep = (
        objName: keyof GenerateStoryStep,
        fieldName: keyof VarConstValue,
        value: boolean | string | number,
    ) => {
        let newObj = JSON.parse(JSON.stringify(step[objName])) as VarConstValue;
        (newObj[fieldName] as any) = value;
        updateStep(stepId, objName, newObj);
    };

    const updateStoryParams = (
        objName: keyof StoryParams,
        fieldName: keyof VarConstValue,
        value: boolean | string | number,
        index: number,
    ) => {
        let StoryParams = JSON.parse(JSON.stringify(step.storyParams));
        StoryParams[index][objName][fieldName] = value;
        updateStep(stepId, 'storyParams', StoryParams);
    };

    const updateDataSources = (
        objName: keyof GenStoryDashboardDataSource | keyof GenStorySurveyDataSource,
        fieldName: keyof VarConstValue,
        value: boolean | string | number,
        index: number,
    ) => {
        let dataSources = JSON.parse(JSON.stringify(step.dataSources));
        dataSources[index][objName][fieldName] = value;
        updateStep(stepId, 'dataSources', dataSources);
    };

    const updateDataSourcesDisabledVar = (index: number, value: boolean | string | number) => {
        let dataSources = JSON.parse(JSON.stringify(step.dataSources));
        dataSources[index]['dataSourceDisabledVar'] = value;
        updateStep(stepId, 'dataSources', dataSources);
    };

    return (
        <Box>
            <FormControl>
                <FormLabel>Story Name</FormLabel>
                <Input
                    placeholder="Enter story name"
                    type="text"
                    bg="white"
                    autoComplete="off"
                    value={step.storyName ? step.storyName : ''}
                    onChange={({ target: { value } }: any) => {
                        updateStep(stepId, 'storyName', value);
                    }}
                />
            </FormControl>

            <FormControl>
                <FormLabel>Processing Type</FormLabel>
                <Select
                    color={'black'}
                    value={step.processingType}
                    backgroundColor="white"
                    onChange={({ target: { value } }: any) => {
                        updateStep(stepId, 'processingType', value);
                    }}
                >
                    <option value="" disabled={true}>
                        Select processing type
                    </option>
                    <option value="static">Static</option>
                    <option value="dynamic">Dynamic</option>
                </Select>
            </FormControl>

            <FormControl mt={2}>
                <FormLabel>Story Id</FormLabel>
                <Flex>
                    <Checkbox
                        mr={2}
                        isChecked={step.storyId?.isFromVar}
                        onChange={({ target }: any) => {
                            updateGenStoryStep('storyId', 'isFromVar', target.checked);
                        }}
                    >
                        <Text fontSize={'xs'} w={'60px'}>
                            From Var?
                        </Text>
                    </Checkbox>
                    {step.storyId?.isFromVar ? (
                        <VarSelect
                            value={step.storyId?.varId ?? ''}
                            filterByType={['STRING']}
                            onChange={(value) => updateGenStoryStep('storyId', 'varId', value)}
                        />
                    ) : (
                        <Input
                            placeholder="Enter story name"
                            type="text"
                            bg="white"
                            autoComplete="off"
                            value={step.storyId?.constValue ? step.storyId?.constValue : ''}
                            onChange={({ target: { value } }: any) => {
                                updateGenStoryStep('storyId', 'constValue', value);
                            }}
                        />
                    )}
                </Flex>
            </FormControl>

            <FormControl mt={2}>
                <FormLabel>Story Code</FormLabel>
                <Flex>
                    <Checkbox
                        mr={2}
                        isChecked={step.storyCode?.isFromVar}
                        onChange={({ target }: any) => {
                            updateGenStoryStep('storyCode', 'isFromVar', target.checked);
                        }}
                    >
                        <Text fontSize={'xs'} w={'60px'}>
                            From Var?
                        </Text>
                    </Checkbox>
                    {step.storyCode?.isFromVar ? (
                        <VarSelect
                            value={step.storyCode?.varId ?? ''}
                            filterByType={['STRING']}
                            onChange={(value) => updateGenStoryStep('storyCode', 'varId', value)}
                        />
                    ) : (
                        <Input
                            placeholder="Enter story name"
                            type="text"
                            bg="white"
                            autoComplete="off"
                            value={step.storyCode?.constValue ? step.storyCode?.constValue : ''}
                            onChange={({ target: { value } }: any) => {
                                updateGenStoryStep('storyCode', 'constValue', value);
                            }}
                        />
                    )}
                </Flex>
            </FormControl>

            <FormControl mt={2}>
                <FormLabel>Url target var</FormLabel>
                <VarSelect
                    value={step.urlTargetVarId ?? ''}
                    filterByType={['STRING']}
                    onChange={(value) => updateStep(stepId, 'urlTargetVarId', value)}
                />
            </FormControl>

            <FormControl mt={2}>
                <FormLabel>Presentation story step</FormLabel>
                <Select
                    color={step.presentationStoryStepId == '' ? 'grey' : 'black'}
                    value={step.presentationStoryStepId}
                    backgroundColor="white"
                    onChange={({ target: { value } }: any) => {
                        updateStep(stepId, 'presentationStoryStepId', value);
                    }}
                >
                    <option value="" disabled={false}>
                        Select Step
                    </option>
                    {Object.keys(pipeline.steps).map((id) => {
                        if (id == stepId || !pipeline.steps[id].name) {
                            return;
                        }

                        return (
                            <option key={id} value={id}>
                                {pipeline.steps[id].name}
                            </option>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl mt={2}>
                <FormLabel>Story params</FormLabel>
                <Stack>
                    {step.storyParams.map((paramElem, index) => {
                        //HERE
                        const paramKey = paramElem.key;
                        const paramValue = paramElem.value;
                        return (
                            <Box borderWidth="1px" borderRadius="md" padding={2} key={index} position="relative" mb={2}>
                                <Button
                                    backgroundColor="white"
                                    _hover={{ color: '#f0706e' }}
                                    _focus={{ backgroundColor: 'white' }}
                                    _active={{ backgroundColor: 'white' }}
                                    color={'grey'}
                                    leftIcon={<DeleteIcon />}
                                    position="absolute"
                                    top={'0px'}
                                    left={'0px'}
                                    zIndex={10}
                                    padding={'0 !important'}
                                    onClick={() => {
                                        let storyParams = JSON.parse(JSON.stringify(step.storyParams));
                                        storyParams.splice(index, 1);
                                        updateStep(stepId, 'storyParams', storyParams);
                                    }}
                                />
                                <Flex w={'100%'} mr={4}>
                                    <Checkbox
                                        isChecked={paramKey?.isFromVar}
                                        mr={4}
                                        mt={10}
                                        onChange={({ target: { checked } }: any) => {
                                            updateStoryParams('key', 'isFromVar', checked, index);
                                        }}
                                    />
                                    <Box minW={'350px'}>
                                        <Text mb={1} mt={4}>
                                            Param key
                                        </Text>
                                        {paramKey?.isFromVar ? (
                                            <VarSelect
                                                value={paramKey?.varId ?? ''}
                                                placeholder="Source"
                                                filterByType={['STRING']}
                                                onChange={(value) => updateStoryParams('key', 'varId', value, index)}
                                            />
                                        ) : (
                                            <Input
                                                backgroundColor="white"
                                                placeholder="Param key name"
                                                alignSelf="center"
                                                value={paramKey?.constValue}
                                                onChange={({ target: { value } }: any) => {
                                                    updateStoryParams('key', 'constValue', value, index);
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Checkbox
                                        isChecked={paramValue?.isFromVar}
                                        ml={4}
                                        mr={4}
                                        mt={10}
                                        onChange={({ target: { checked } }: any) => {
                                            updateStoryParams('value', 'isFromVar', checked, index);
                                        }}
                                    />
                                    <Box minW={'350px'}>
                                        <Text mb={1} mt={4}>
                                            Param value
                                        </Text>
                                        {paramValue?.isFromVar ? (
                                            <VarSelect
                                                value={paramValue?.varId ?? ''}
                                                placeholder="Source"
                                                filterByType={['STRING']}
                                                onChange={(value) => updateStoryParams('value', 'varId', value, index)}
                                            />
                                        ) : (
                                            <Input
                                                backgroundColor="white"
                                                placeholder="value"
                                                alignSelf="center"
                                                value={paramValue?.constValue}
                                                onChange={({ target: { value } }: any) => {
                                                    updateStoryParams('value', 'constValue', value, index);
                                                }}
                                            />
                                        )}
                                    </Box>
                                    {/* <Box ml={4} width={'100%'}>
                                        <Text mb={1} mt={4} ml={4}>
                                            Datasource type
                                        </Text>
                                        <Select
                                            alignSelf="center"
                                            backgroundColor="white"
                                            value={dataSourceType ? dataSourceType : 'survey'}
                                            onChange={({ target: { value } }: any) => {
                                                let dataSources = JSON.parse(JSON.stringify(step.dataSources));
                                                dataSources[index].dataSourceType = value;
                                                updateStep(stepId, 'dataSources', dataSources);
                                            }}
                                        >
                                            <option>Survey</option>
                                            <option disabled>Spreadsheet</option>
                                            <option disabled>Parameter</option>
                                        </Select>
                                    </Box> */}
                                </Flex>
                            </Box>
                            // <Flex key={index}>

                            //     <Button
                            //         backgroundColor="white"
                            //         _hover={{ color: '#f0706e' }}
                            //         _focus={{ backgroundColor: 'white' }}
                            //         _active={{ backgroundColor: 'white' }}
                            //         color={'grey'}
                            //         leftIcon={<DeleteIcon />}
                            //         //

                            //         onClick={() => {
                            //             let storyParams = JSON.parse(JSON.stringify(step.storyParams));
                            //             storyParams.splice(index, 1);
                            //             updateStep(stepId, 'storyParams', storyParams);
                            //         }}
                            //     ></Button>
                            //     <Input
                            //         backgroundColor="white"
                            //         placeholder="key"
                            //         focusBorderColor={'white'}
                            //         value={paramElem.key}
                            //         onChange={({ target: { value } }: any) => {
                            //             let storyParams = JSON.parse(JSON.stringify(step.storyParams));
                            //             storyParams[index].key = value;
                            //             updateStep(stepId, 'storyParams', storyParams);
                            //         }}
                            //     />
                            //     <Spacer minW={1} />
                            //     <Select
                            //         value={paramElem.valueVarId}
                            //         color={paramElem.valueVarId == '' ? 'grey' : 'black'}
                            //         backgroundColor="white"
                            //         onChange={({ target: { value } }: any) => {
                            //             let storyParams = JSON.parse(JSON.stringify(step.storyParams));
                            //             storyParams[index].valueVarId = value;
                            //             updateStep(stepId, 'storyParams', storyParams);
                            //         }}
                            //     >
                            //         <option value="" disabled={true}>
                            //             Var
                            //         </option>
                            //         {Object.keys(pipeline.vars).map((key) => {
                            //             const { type, name } = pipeline.vars[key];

                            //             return (
                            //                 <option key={key} value={key}>
                            //                     {name}
                            //                 </option>
                            //             );
                            //         })}
                            //     </Select>
                            // </Flex>
                        );
                    })}

                    <Button
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'white', color: 'black' }}
                        _focus={{ backgroundColor: 'white' }}
                        _active={{ backgroundColor: 'white' }}
                        color={'grey'}
                        leftIcon={<AddIcon />}
                        onClick={() => {
                            let storyParams = JSON.parse(JSON.stringify(step.storyParams));
                            let newObj = {
                                isFromVar: false,
                                varId: '',
                                constValue: '',
                            };
                            storyParams.push({
                                // key: '',
                                // valueIsVar: true,
                                // value: '',
                                // valueVarId: '',
                                key: newObj,
                                value: newObj,
                            });
                            updateStep(stepId, 'storyParams', storyParams);
                        }}
                    >
                        Add param
                    </Button>
                </Stack>
            </FormControl>
            <FormControl mt={4}>
                <FormLabel>Data Sources</FormLabel>
                <Stack>
                    {step.dataSources?.map((dataSource, index) => {
                        const { name, dataSourceType } = dataSource;
                        let datasourceId: VarConstValue;
                        let datasourceRef: VarConstValue;
                        if (dataSource.dataSourceType === 'survey') {
                            datasourceId = dataSource.surveyId;
                            datasourceRef = dataSource.surveyRef;
                        } else if (dataSource.dataSourceType === 'dashboard') {
                            datasourceId = dataSource.dashboardId;
                            datasourceRef = dataSource.dashboardRef;
                        } else {
                            return;
                        }

                        return (
                            <Box borderWidth="1px" borderRadius="md" padding={2} key={index} position="relative" mb={2}>
                                <Button
                                    backgroundColor="white"
                                    _hover={{ color: '#f0706e' }}
                                    _focus={{ backgroundColor: 'white' }}
                                    _active={{ backgroundColor: 'white' }}
                                    color={'grey'}
                                    leftIcon={<DeleteIcon />}
                                    position="absolute"
                                    top={'0px'}
                                    left={'0px'}
                                    zIndex={10}
                                    padding={'0 !important'}
                                    onClick={() => {
                                        let dataSources = JSON.parse(JSON.stringify(step.dataSources));
                                        dataSources.splice(index, 1);
                                        updateStep(stepId, 'dataSources', dataSources);
                                    }}
                                />

                                {/* Data source */}

                                <Flex w={'100%'} mr={4}>
                                    <Checkbox
                                        isChecked={name?.isFromVar}
                                        mr={4}
                                        mt={10}
                                        onChange={({ target: { checked } }: any) => {
                                            updateDataSources('name', 'isFromVar', checked, index);
                                        }}
                                    />
                                    <Box minW={'400px'}>
                                        <Text mb={1} mt={4}>
                                            Datasource name
                                        </Text>
                                        {name?.isFromVar ? (
                                            <VarSelect
                                                value={name?.varId ?? ''}
                                                placeholder="Source"
                                                filterByType={['STRING']}
                                                onChange={(value) => updateDataSources('name', 'varId', value, index)}
                                            />
                                        ) : (
                                            <Input
                                                backgroundColor="white"
                                                placeholder="Data source name"
                                                alignSelf="center"
                                                value={name?.constValue}
                                                onChange={({ target: { value } }: any) => {
                                                    updateDataSources('name', 'constValue', value, index);
                                                }}
                                            />
                                        )}
                                    </Box>
                                    <Box ml={4} width={'100%'}>
                                        <Text mb={1} mt={4} ml={4}>
                                            Datasource type
                                        </Text>
                                        <Select
                                            alignSelf="center"
                                            backgroundColor="white"
                                            value={dataSourceType ? dataSourceType : 'survey'}
                                            onChange={({ target: { value } }: any) => {
                                                let dataSources = JSON.parse(JSON.stringify(step.dataSources));
                                                dataSources[index].dataSourceType = value;
                                                updateStep(stepId, 'dataSources', dataSources);
                                            }}
                                        >
                                            <option value="survey">Survey</option>
                                            <option value="dashboard">Dashboard</option>
                                        </Select>
                                    </Box>
                                </Flex>

                                {/* Datasource resource id */}
                                <Flex w={'100%'}>
                                    <Checkbox
                                        mt={10}
                                        isChecked={datasourceId?.isFromVar}
                                        onChange={({ target: { checked } }: any) => {
                                            updateDataSources(
                                                dataSourceType === 'survey' ? 'surveyId' : 'dashboardId',
                                                'isFromVar',
                                                checked,
                                                index,
                                            );
                                        }}
                                    />
                                    <Box ml={4} width={'100%'} minW={'400px'}>
                                        <Text mb={1} mt={4}>
                                            {dataSourceType === 'survey' ? 'Survey Id' : 'Dashboard id'}
                                        </Text>

                                        {datasourceId?.isFromVar ? (
                                            <VarSelect
                                                value={datasourceId?.varId ?? ''}
                                                placeholder="Source"
                                                filterByType={['STRING']}
                                                onChange={(value) =>
                                                    updateDataSources(
                                                        dataSourceType === 'survey' ? 'surveyId' : 'dashboardId',
                                                        'varId',
                                                        value,
                                                        index,
                                                    )
                                                }
                                            />
                                        ) : (
                                            <Input
                                                backgroundColor="white"
                                                placeholder={dataSourceType === 'survey' ? 'Survey Id' : 'Dashboard Id'}
                                                value={datasourceId?.constValue}
                                                onChange={({ target: { value } }: any) => {
                                                    updateDataSources(
                                                        dataSourceType === 'survey' ? 'surveyId' : 'dashboardId',
                                                        'constValue',
                                                        value,
                                                        index,
                                                    );
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Flex>

                                <Box pl={8} pb={4} width={'100%'}>
                                    <Text mb={1} mt={4}>
                                        {dataSourceType === 'survey' ? 'Survey Ref' : 'Dashboard Ref'}
                                    </Text>
                                    <VarSelect
                                        value={datasourceRef?.varId ?? ''}
                                        placeholder={dataSourceType === 'survey' ? 'Survey Ref' : 'Dashboard Ref'}
                                        filterByType={['REF']}
                                        onChange={(value) =>
                                            updateDataSources(
                                                dataSourceType === 'survey' ? 'surveyRef' : 'dashboardRef',
                                                'varId',
                                                value,
                                                index,
                                            )
                                        }
                                    />
                                </Box>

                                <HStack pl={8} pb={4} width={'100%'} mt={4}>
                                    <Text mb={1} w={'230px'}>
                                        Disable data source var:
                                    </Text>
                                    <VarSelect
                                        value={dataSource.dataSourceDisabledVar ?? ''}
                                        placeholder="Select Var"
                                        filterByType={['BOOLEAN']}
                                        width="100%"
                                        onChange={(value) => updateDataSourcesDisabledVar(index, value)}
                                    />
                                </HStack>
                            </Box>
                        );
                    })}

                    <Button
                        backgroundColor="white"
                        fontSize={'sm'}
                        _hover={{ backgroundColor: 'white', color: 'black' }}
                        _focus={{ backgroundColor: 'white' }}
                        _active={{ backgroundColor: 'white' }}
                        color={'grey'}
                        leftIcon={<AddIcon />}
                        onClick={() => {
                            let dataSources = [];
                            if (step.dataSources) {
                                dataSources = JSON.parse(JSON.stringify(step.dataSources));
                            }
                            let obj = {
                                isFromVar: false,
                                varId: '',
                                constValue: '',
                            };
                            dataSources.push({
                                name: obj,
                                dataSourceType: 'survey',
                                surveyId: obj,
                                surveyRef: obj,
                                dashboardId: obj,
                                dashboardRef: {
                                    isFromVar: true,
                                    varId: '',
                                    constValue: '',
                                },
                            });
                            updateStep(stepId, 'dataSources', dataSources);
                        }}
                    >
                        Add Data Source
                    </Button>
                </Stack>
            </FormControl>
        </Box>
    );
}
